import { useState, useEffect } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Shield,
  Loader2,
  Users,
  LayoutDashboard,
  HeartHandshake,
  Package,
  Truck,
  Building2,
  FileText,
  ShoppingCart,
  UserCog,
  Check,
  X,
  Eye,
} from 'lucide-react';

// Module icons mapping
const MODULE_ICONS = {
  dashboard: LayoutDashboard,
  attention: HeartHandshake,
  beneficiaries: Users,
  inventory: Package,
  deliveries: Truck,
  suppliers: Building2,
  invoices: FileText,
  purchase_orders: ShoppingCart,
  users: UserCog,
  reports: FileText,
};

// Module labels in Spanish
const MODULE_LABELS = {
  dashboard: 'Dashboard',
  attention: 'Atención al Cliente',
  beneficiaries: 'Beneficiarios',
  inventory: 'Inventario',
  deliveries: 'Entregas',
  suppliers: 'Proveedores',
  invoices: 'Facturas',
  purchase_orders: 'Órdenes de Compra',
  users: 'Usuarios',
  reports: 'Reportes',
};

// Role colors
const ROLE_COLORS = {
  admin: 'bg-purple-100 text-purple-700 border-purple-200',
  social_worker: 'bg-teal-100 text-teal-700 border-teal-200',
  warehouse: 'bg-amber-100 text-amber-700 border-amber-200',
  finance: 'bg-blue-100 text-blue-700 border-blue-200',
};

const RolesPermissions = () => {
  const { user } = useAuth();
  const [roles, setRoles] = useState({});
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchData = async () => {
    try {
      const [rolesRes, usersRes] = await Promise.all([
        api.get('/roles/all'),
        api.get('/users'),
      ]);
      setRoles(rolesRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('No tiene permisos para ver esta página');
      } else {
        toast.error('Error al cargar datos');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const getUserCountByRole = (role) => {
    return users.filter(u => u.role === role).length;
  };

  const getAllModules = () => {
    const allModules = new Set();
    Object.values(roles).forEach(role => {
      role.modules?.forEach(m => allModules.add(m));
    });
    return Array.from(allModules);
  };

  if (user?.role !== 'admin') {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-slate-400">
        <Shield className="w-12 h-12 mb-4" />
        <p className="text-lg font-medium">Acceso Restringido</p>
        <p className="text-sm">Solo administradores pueden acceder a esta sección</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  const allModules = getAllModules();

  return (
    <div className="space-y-6" data-testid="roles-permissions-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">
          Roles y Permisos
        </h1>
        <p className="text-slate-500 mt-1">
          Configuración de perfiles y accesos del sistema
        </p>
      </div>

      {/* Roles Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.entries(roles).map(([roleKey, roleData]) => {
          const userCount = getUserCountByRole(roleKey);
          return (
            <Card key={roleKey} className="card-hover cursor-pointer" onClick={() => { setSelectedRole(roleKey); setDialogOpen(true); }}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-3 rounded-xl ${ROLE_COLORS[roleKey]?.split(' ')[0]} ${ROLE_COLORS[roleKey]?.split(' ')[1]}`}>
                    <Shield className="w-6 h-6" />
                  </div>
                  <Badge className={ROLE_COLORS[roleKey]}>
                    {userCount} usuarios
                  </Badge>
                </div>
                <h3 className="font-semibold text-lg text-slate-900 font-['Manrope']">
                  {roleData.label}
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  {roleData.modules?.length || 0} módulos habilitados
                </p>
                <div className="flex flex-wrap gap-1 mt-3">
                  {roleData.modules?.slice(0, 3).map(mod => {
                    const Icon = MODULE_ICONS[mod] || Package;
                    return (
                      <div key={mod} className="p-1.5 bg-slate-100 rounded" title={MODULE_LABELS[mod]}>
                        <Icon className="w-3.5 h-3.5 text-slate-600" />
                      </div>
                    );
                  })}
                  {roleData.modules?.length > 3 && (
                    <div className="p-1.5 bg-slate-100 rounded text-xs text-slate-600 font-medium">
                      +{roleData.modules.length - 3}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Permissions Matrix */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold font-['Manrope']">
            Matriz de Permisos
          </CardTitle>
          <CardDescription>
            Vista general de los accesos por rol
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-48">Módulo</TableHead>
                  {Object.entries(roles).map(([roleKey, roleData]) => (
                    <TableHead key={roleKey} className="text-center min-w-[120px]">
                      <div className="flex flex-col items-center gap-1">
                        <Badge className={`${ROLE_COLORS[roleKey]} text-xs`}>
                          {roleData.label}
                        </Badge>
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {allModules.map(module => {
                  const Icon = MODULE_ICONS[module] || Package;
                  return (
                    <TableRow key={module}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-slate-100 rounded-lg">
                            <Icon className="w-4 h-4 text-slate-600" />
                          </div>
                          <span className="font-medium">{MODULE_LABELS[module] || module}</span>
                        </div>
                      </TableCell>
                      {Object.entries(roles).map(([roleKey, roleData]) => {
                        const hasAccess = roleData.modules?.includes(module);
                        return (
                          <TableCell key={roleKey} className="text-center">
                            {hasAccess ? (
                              <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100">
                                <Check className="w-5 h-5 text-emerald-600" />
                              </div>
                            ) : (
                              <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100">
                                <X className="w-5 h-5 text-slate-400" />
                              </div>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Users by Role */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold font-['Manrope']">
            Usuarios por Rol
          </CardTitle>
          <CardDescription>
            Lista de usuarios asignados a cada perfil
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(roles).map(([roleKey, roleData]) => {
              const roleUsers = users.filter(u => u.role === roleKey);
              return (
                <div key={roleKey} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Badge className={ROLE_COLORS[roleKey]}>
                        {roleData.label}
                      </Badge>
                      <span className="text-sm text-slate-500">
                        ({roleUsers.length} usuarios)
                      </span>
                    </div>
                  </div>
                  {roleUsers.length > 0 ? (
                    <div className="space-y-2">
                      {roleUsers.map(u => (
                        <div key={u.user_id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-semibold text-slate-600">
                            {u.name?.charAt(0) || 'U'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{u.name}</p>
                            <p className="text-xs text-slate-500 truncate">{u.email}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-4">
                      No hay usuarios con este rol
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Role Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              <Shield className="w-5 h-5" />
              {roles[selectedRole]?.label}
            </DialogTitle>
          </DialogHeader>
          {selectedRole && roles[selectedRole] && (
            <div className="space-y-6 mt-4">
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Módulos Habilitados</h4>
                <div className="space-y-2">
                  {roles[selectedRole].modules?.map(mod => {
                    const Icon = MODULE_ICONS[mod] || Package;
                    return (
                      <div key={mod} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <div className="p-2 bg-white rounded-lg shadow-sm">
                          <Icon className="w-4 h-4 text-teal-600" />
                        </div>
                        <span className="font-medium text-slate-700">{MODULE_LABELS[mod] || mod}</span>
                        <Check className="w-4 h-4 text-emerald-500 ml-auto" />
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Acciones Permitidas</h4>
                <div className="flex flex-wrap gap-2">
                  {roles[selectedRole].actions?.map(action => (
                    <Badge key={action} variant="outline" className="capitalize">
                      {action.replace('_', ' ')}
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3">
                  Usuarios con este Rol ({getUserCountByRole(selectedRole)})
                </h4>
                <div className="max-h-40 overflow-y-auto space-y-2">
                  {users.filter(u => u.role === selectedRole).map(u => (
                    <div key={u.user_id} className="flex items-center gap-2 text-sm">
                      <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold">
                        {u.name?.charAt(0)}
                      </div>
                      <span>{u.name}</span>
                      <span className="text-slate-400">({u.email})</span>
                    </div>
                  ))}
                  {users.filter(u => u.role === selectedRole).length === 0 && (
                    <p className="text-sm text-slate-400">No hay usuarios</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RolesPermissions;
